import { debug } from './debug';
/**
 * Locale and Currency Formatting Utilities
 *
 * Provides locale detection and currency/date formatting.
 * Formatting locale is derived from the user's i18n language + browser region.
 * Timezone-based mapping is kept as a fallback for server-side use.
 */

/**
 * Default region for each supported language.
 * Used when browser region can't be determined.
 */
const LANGUAGE_DEFAULT_REGION = {
  en: 'US',
  de: 'DE',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  hu: 'HU',
};

/**
 * True if `tag` is well-formed enough for Intl to accept.
 * Intl.getCanonicalLocales throws RangeError on malformed input.
 */
const isUsableLocale = (tag) => {
  try {
    Intl.getCanonicalLocales(tag);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Extract the region subtag from a browser locale string.
 *
 * Ported by hand from clickandshare-frontend-v3 (e51b61b). navigator.language is
 * not always a bare `xx-YY`: POSIX-style modifiers ('de-DE@euro') and Unicode
 * extensions ('de-DE-u-ca-gregory') both occur in the wild. The previous
 * implementation took the LAST hyphen-separated part and upper-cased it, so
 * those became 'DE@EURO' and 'GREGORY' — neither is a region, and both produce a
 * tag Intl rejects.
 *
 * A region subtag is two letters (DE) or three digits (419).
 *
 * @param {string} browserLocale
 * @returns {string|null} region subtag, or null if there isn't a usable one
 */
const extractRegion = (browserLocale) => {
  // Drop any @modifier, then walk the subtags after the language.
  const parts = String(browserLocale).split('@')[0].split('-');
  for (let i = 1; i < parts.length; i++) {
    // A single-character subtag is an extension singleton (the 'u' in
    // '-u-ca-gregory'); everything after it is extension data, not a region.
    if (parts[i].length === 1) break;
    if (/^[A-Za-z]{2}$/.test(parts[i])) return parts[i].toUpperCase();
    if (/^[0-9]{3}$/.test(parts[i])) return parts[i];
  }
  return null;
};

/**
 * Build a formatting locale from the user's i18n language and browser region.
 *
 * Always returns a tag Intl accepts, so callers can format without a guard.
 *
 * @param {string} language - 2-letter i18n language code (e.g. 'de', 'en')
 * @returns {string} BCP 47 locale tag (e.g. 'de-AT', 'en-GB')
 */
export const getFormattingLocale = (language) => {
  const lang = language || 'en';

  // Extract region from browser locale (e.g. 'de-AT' → 'AT')
  try {
    const region = extractRegion(navigator.language || navigator.userLanguage || '');
    if (region) {
      const tag = `${lang}-${region}`;
      if (isUsableLocale(tag)) return tag;
    }
  } catch (e) {
    // navigator not available (SSR)
  }

  // Fallback to default region for language. Validated too, so a caller passing
  // a junk `language` can't produce a tag that throws downstream.
  const fallback = `${lang}-${LANGUAGE_DEFAULT_REGION[lang] || 'US'}`;
  return isUsableLocale(fallback) ? fallback : 'en-US';
};

/**
 * Mapping of IANA timezone identifiers to locale codes
 * Used for currency formatting, date formatting, and number formatting
 */
export const TIMEZONE_TO_LOCALE = {
  // Americas - United States
  'America/New_York': 'en-US',
  'America/Los_Angeles': 'en-US',
  'America/Chicago': 'en-US',
  'America/Denver': 'en-US',
  'America/Phoenix': 'en-US',
  'America/Anchorage': 'en-US',
  'America/Honolulu': 'en-US',

  // Americas - Canada
  'America/Toronto': 'en-CA',
  'America/Vancouver': 'en-CA',
  'America/Montreal': 'fr-CA',
  'America/Edmonton': 'en-CA',
  'America/Winnipeg': 'en-CA',
  'America/Halifax': 'en-CA',

  // Americas - Latin America
  'America/Mexico_City': 'es-MX',
  'America/Sao_Paulo': 'pt-BR',
  'America/Buenos_Aires': 'es-AR',
  'America/Santiago': 'es-CL',
  'America/Bogota': 'es-CO',
  'America/Lima': 'es-PE',

  // Europe - Western
  'Europe/London': 'en-GB',
  'Europe/Dublin': 'en-IE',
  'Europe/Paris': 'fr-FR',
  'Europe/Madrid': 'es-ES',
  'Europe/Lisbon': 'pt-PT',
  'Europe/Amsterdam': 'nl-NL',
  'Europe/Brussels': 'nl-BE',
  'Europe/Luxembourg': 'fr-LU',

  // Europe - Central
  'Europe/Berlin': 'de-DE',
  'Europe/Vienna': 'de-AT',
  'Europe/Zurich': 'de-CH',
  'Europe/Rome': 'it-IT',
  'Europe/Prague': 'cs-CZ',
  'Europe/Warsaw': 'pl-PL',
  'Europe/Budapest': 'hu-HU',

  // Europe - Northern
  'Europe/Stockholm': 'sv-SE',
  'Europe/Oslo': 'nb-NO',
  'Europe/Copenhagen': 'da-DK',
  'Europe/Helsinki': 'fi-FI',
  'Europe/Reykjavik': 'is-IS',

  // Europe - Southern
  'Europe/Athens': 'el-GR',
  'Europe/Bucharest': 'ro-RO',
  'Europe/Sofia': 'bg-BG',

  // Europe - Eastern
  'Europe/Moscow': 'ru-RU',
  'Europe/Kiev': 'uk-UA',
  'Europe/Tallinn': 'et-EE',
  'Europe/Riga': 'lv-LV',
  'Europe/Vilnius': 'lt-LT',

  // Asia - East
  'Asia/Tokyo': 'ja-JP',
  'Asia/Seoul': 'ko-KR',
  'Asia/Shanghai': 'zh-CN',
  'Asia/Hong_Kong': 'zh-HK',
  'Asia/Taipei': 'zh-TW',

  // Asia - Southeast
  'Asia/Singapore': 'en-SG',
  'Asia/Bangkok': 'th-TH',
  'Asia/Jakarta': 'id-ID',
  'Asia/Manila': 'en-PH',
  'Asia/Kuala_Lumpur': 'ms-MY',

  // Asia - South
  'Asia/Dubai': 'ar-AE',
  'Asia/Kolkata': 'en-IN',
  'Asia/Karachi': 'ur-PK',

  // Oceania
  'Australia/Sydney': 'en-AU',
  'Australia/Melbourne': 'en-AU',
  'Australia/Brisbane': 'en-AU',
  'Australia/Perth': 'en-AU',
  'Pacific/Auckland': 'en-NZ',

  // Africa
  'Africa/Johannesburg': 'en-ZA',
  'Africa/Cairo': 'ar-EG',
  'Africa/Lagos': 'en-NG',
  'Africa/Nairobi': 'en-KE',
};

/**
 * Get locale code from timezone identifier
 *
 * @param {string} timezone - IANA timezone identifier (e.g., 'Europe/Berlin')
 * @returns {string} Locale code (e.g., 'de-DE')
 */
export const getLocaleFromTimezone = (timezone) => {
  if (!timezone) {
    debug.warn('No timezone provided, defaulting to en-US');
    return 'en-US';
  }

  const locale = TIMEZONE_TO_LOCALE[timezone];

  if (!locale) {
    debug.warn(`No locale mapping found for timezone: ${timezone}, defaulting to en-US`);
    return 'en-US';
  }

  return locale;
};

/**
 * Format currency amount using merchant's locale and currency
 * Uses Intl.NumberFormat for automatic handling of:
 * - Symbol positioning (before/after)
 * - Thousands separators (comma/period/space)
 * - Decimal separators
 *
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @param {string} locale - Locale code (e.g., 'en-US', 'de-DE')
 * @returns {string} Formatted currency string (e.g., '$100.00', '100,00 €')
 */
export const formatCurrency = (amount, currencyCode = 'USD', locale = 'en-US') => {
  if (amount === null || amount === undefined) {
    debug.warn('formatCurrency called with null/undefined amount');
    return formatCurrency(0, currencyCode, locale);
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    }).format(amount);
  } catch (error) {
    console.error(`Error formatting currency: ${error.message}`, {
      amount,
      currencyCode,
      locale
    });
    // Fallback to simple formatting
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
};

/**
 * Format currency amount using merchant data
 * Convenience wrapper that extracts locale and currency from merchant object
 *
 * @param {number} amount - Amount to format
 * @param {object} merchant - Merchant object with timezone and currency_code
 * @returns {string} Formatted currency string
 */
export const formatCurrencyForMerchant = (amount, merchant) => {
  if (!merchant) {
    debug.warn('formatCurrencyForMerchant called without merchant, using defaults');
    return formatCurrency(amount, 'USD', 'en-US');
  }

  const locale = getLocaleFromTimezone(merchant.timezone);
  const currencyCode = merchant.currency_code || 'USD';

  return formatCurrency(amount, currencyCode, locale);
};

/**
 * Format date using merchant's locale
 *
 * @param {Date|string} date - Date to format
 * @param {string} locale - Locale code
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateWithLocale = (date, locale = 'en-US', options = {}) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  const defaultOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options
  };

  try {
    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
  } catch (error) {
    console.error(`Error formatting date: ${error.message}`, { date, locale });
    return dateObj.toLocaleDateString();
  }
};

/**
 * Format date using merchant's timezone-derived locale
 *
 * @param {Date|string} date - Date to format
 * @param {object} merchant - Merchant object with timezone
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateForMerchant = (date, merchant, options = {}) => {
  if (!merchant) {
    return formatDateWithLocale(date, 'en-US', options);
  }

  const locale = getLocaleFromTimezone(merchant.timezone);
  return formatDateWithLocale(date, locale, options);
};

/**
 * Translate a duration ID (e.g. '6h', '2d', '1w') into a localized label.
 *
 * Requires the i18next `t` function so it always uses the current language.
 * Translation keys: duration.hours, duration.days, duration.weeks (with count).
 *
 * @param {string} durationId - Duration code like '6h', '2d', '1w'
 * @param {Function} t - i18next translation function
 * @returns {string} Localized label (e.g. "6 Stunden", "2 days")
 */
export const getDurationLabel = (durationId, t) => {
  if (!durationId) return '';
  const match = durationId.match(/^(\d+)([hdw])$/);
  if (!match) return durationId;

  const [, count, unit] = match;
  const num = parseInt(count);

  if (unit === 'h') return t('duration.hours', { count: num });
  if (unit === 'd') return t('duration.days', { count: num });
  if (unit === 'w') return t('duration.weeks', { count: num });
  return durationId;
};

/**
 * Get currency symbol for a currency code
 * Uses Intl.NumberFormat to extract the symbol
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {string} locale - Locale code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode = 'USD', locale = 'en-US') => {
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    }).format(0);

    // Extract just the symbol by removing digits and common separators
    return formatted.replace(/[\d.,\s]/g, '');
  } catch (error) {
    console.error(`Error getting currency symbol: ${error.message}`);
    return currencyCode;
  }
};
